import React, { useContext, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Autocomplete, Chip, Divider } from '@mui/material';
import { AuthContext } from 'authentication/AuthContext';
import renderInput from 'feature/inventory-management/reconsignments/edit/renderUtils';
import {
  getSingleFilteredOption,
  handleSingleOptionEnterToggle,
} from 'feature/loading-management/utils/autocompleteEnterToggle';
import {
  useGetFilteredProductsQuery,
  useGetUniqueLocationsListQuery,
} from './selfAuthorizationsEndpoints';
import { STATUS_OPTIONS, TYPE_OPTIONS } from './selfAuthFilterConstants';

const renderCollapsedTags = selectedOptions => {
  if (!selectedOptions.length) {
    return null;
  }

  const firstLabel = selectedOptions[0]?.label ?? selectedOptions[0]?.value ?? '';

  return (
    <>
      <span
        style={{
          display: 'inline-block',
          marginLeft: 5,
          marginRight: 8,
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {firstLabel}
      </span>
      {selectedOptions.length > 1 && (
        <Chip label={`+${selectedOptions.length - 1}`} size="small" variant="autocomplete" />
      )}
    </>
  );
};

const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
};

const isValueOptionEqual = (option, value) => option?.value === value?.value;

const SelfAuthorizationsFilters = ({
  affiliateFilter,
  disabled,
  locationFilter,
  productFilter,
  setAffiliateFilter,
  setLocationFilter,
  setProductFilter,
  setStatusFilter,
  setTypeFilter,
  statusFilter,
  typeFilter,
}) => {
  const { affiliates = [] } = useContext(AuthContext);
  const [locationInputValue, setLocationInputValue] = useState('');
  const [productInputValue, setProductInputValue] = useState('');
  const selectedLocationFilter = Array.isArray(locationFilter) ? locationFilter : [];
  const selectedProductFilter = Array.isArray(productFilter) ? productFilter : [];

  const affiliateOptions = useMemo(
    () => affiliates.filter(affiliate => affiliate?.inventoryOwner),
    [affiliates]
  );

  const selectedAffiliateFilter = affiliateFilter || affiliateOptions[0] || null;

  const productParams = {
    'InventoryOwner.CompanyCustomerIdName': selectedAffiliateFilter?.customerIdName,
    'InventoryOwner.DataCompanyId': selectedAffiliateFilter?.dataCompanyId,
  };
  const locationParams = {
    dataCompanyId: selectedAffiliateFilter?.dataCompanyId,
  };

  const { data: locationData = [], isLoading: isLocationsLoading } = useGetUniqueLocationsListQuery(
    locationParams,
    {
      skip: !selectedAffiliateFilter?.dataCompanyId,
    }
  );

  const locationOptions = useMemo(
    () =>
      locationData
        .map(location => ({
          label: location?.nameLong,
          value: location?.id,
        }))
        .filter(option => option.label && option.value),
    [locationData]
  );

  const { data: productData = [], isLoading: isProductsLoading } = useGetFilteredProductsQuery(
    productParams,
    {
      skip: !selectedAffiliateFilter?.customerIdName || !selectedAffiliateFilter?.dataCompanyId,
    }
  );

  const productOptions = useMemo(
    () =>
      productData
        .map(product => ({
          label: product?.productName,
          value: product?.productId,
        }))
        .filter(option => option.label && option.value),
    [productData]
  );

  const locationSingleFilteredOption = useMemo(
    () =>
      getSingleFilteredOption(locationOptions, locationInputValue, option => option?.label ?? ''),
    [locationInputValue, locationOptions]
  );

  const productSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(productOptions, productInputValue, option => option?.label ?? ''),
    [productInputValue, productOptions]
  );

  useEffect(() => {
    if (affiliateOptions.length > 0 && !selectedAffiliateFilter) {
      setAffiliateFilter(affiliateOptions[0]);
    }
  }, [affiliateOptions, selectedAffiliateFilter, setAffiliateFilter]);

  return (
    <>
      {affiliateOptions.length > 1 && (
        <Autocomplete
          autoHighlight
          disableClearable
          disabled={disabled}
          disablePortal
          getOptionLabel={option => option?.customerNameLong ?? ''}
          id="affiliate-filter"
          isOptionEqualToValue={(option, value) =>
            option?.customerIdName === value?.customerIdName &&
            option?.dataCompanyId === value?.dataCompanyId
          }
          onChange={(event, newValue) => setAffiliateFilter(newValue)}
          options={affiliateOptions}
          renderInput={renderInput({
            label: 'Affiliate',
            length: affiliateOptions.length,
            loading: false,
          })}
          value={selectedAffiliateFilter}
        />
      )}
      {affiliateOptions.length > 1 && <Divider sx={{ my: 2 }} />}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={disabled}
        disablePortal
        getOptionLabel={option => option?.label ?? ''}
        id="location-filter"
        inputValue={locationInputValue}
        isOptionEqualToValue={isValueOptionEqual}
        multiple
        onChange={(event, newValue) => {
          setLocationFilter(newValue);
        }}
        onInputChange={(event, newInputValue) => setLocationInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            locationSingleFilteredOption,
            selectedLocationFilter,
            setLocationFilter,
            isValueOptionEqual
          )
        }
        openOnFocus
        options={locationOptions}
        renderInput={renderInput({
          label: 'Locations',
          length: locationOptions.length,
          loading: isLocationsLoading,
          selectedCount: selectedLocationFilter.length,
        })}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={selectedLocationFilter}
      />
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={disabled}
        disablePortal
        getOptionLabel={option => option?.label ?? ''}
        id="product-filter"
        inputValue={productInputValue}
        isOptionEqualToValue={isValueOptionEqual}
        multiple
        onChange={(event, newValue) => setProductFilter(newValue)}
        onInputChange={(event, newInputValue) => setProductInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            productSingleFilteredOption,
            selectedProductFilter,
            setProductFilter,
            isValueOptionEqual
          )
        }
        openOnFocus
        options={productOptions}
        renderInput={renderInput({
          label: 'Products',
          length: productOptions.length,
          loading: isProductsLoading,
          selectedCount: selectedProductFilter.length,
        })}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={selectedProductFilter}
      />
      <Autocomplete
        autoHighlight
        disableClearable
        disabled={disabled}
        disablePortal
        getOptionLabel={option => option?.label ?? ''}
        id="type-filter"
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        onChange={(event, newValue) => setTypeFilter(newValue)}
        options={TYPE_OPTIONS}
        renderInput={renderInput({
          label: 'Type',
          length: TYPE_OPTIONS.length,
          loading: false,
        })}
        value={typeFilter}
      />
      <Autocomplete
        autoHighlight
        disableClearable
        disabled={disabled}
        disablePortal
        getOptionLabel={option => option?.label ?? ''}
        id="status-filter"
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        onChange={(event, newValue) => setStatusFilter(newValue)}
        options={STATUS_OPTIONS}
        renderInput={renderInput({
          label: 'Status',
          length: STATUS_OPTIONS.length,
          loading: false,
        })}
        value={statusFilter}
      />
    </>
  );
};

SelfAuthorizationsFilters.propTypes = {
  affiliateFilter: shape({
    customerIdName: string,
    customerNameLong: string,
    dataCompanyId: string,
  }),
  disabled: bool,
  locationFilter: arrayOf(shape({ label: string, value: string })),
  productFilter: arrayOf(shape({ label: string, value: string })),
  setAffiliateFilter: func.isRequired,
  setLocationFilter: func.isRequired,
  setProductFilter: func.isRequired,
  setStatusFilter: func.isRequired,
  setTypeFilter: func.isRequired,
  statusFilter: shape({ label: string, value: string }).isRequired,
  typeFilter: shape({ label: string, value: string }).isRequired,
};

SelfAuthorizationsFilters.defaultProps = {
  affiliateFilter: null,
  disabled: false,
  locationFilter: [],
  productFilter: [],
};

export default SelfAuthorizationsFilters;
